import { startCase } from 'lodash'
import { ContentClassificationView } from '../../../api/client'
import { Queue } from '../../../models/Moderation'
import { getModerationStyles } from '../../../util/moderationStyles'

type Props = {
    queue: Queue
    classification: ContentClassificationView
    accountId: number
    contentId: number
}

export default function ClassificationDisplay(props: Props) {
    const { queue, classification, accountId, contentId } = props

    // The search returns the label, so a pill needs no side query; the fallback
    // only covers a classification that arrives without one.
    const classifierName =
        classification.classifierDisplayName ?? startCase(classification.classifierName)

    const SENTIMENT_NAME = 'sentiment'

    const styles = getModerationStyles(queue)
    const textColour = styles.text
    const backgroundColour = styles.background
    const indicatorColour = styles.indicator
    const borderColour = styles.border

    // Generate the display values
    //
    // `!= null` rather than a truthiness test: a score of exactly 0 is a real
    // score, and treating it as absent left the tooltip reading "... classified
    // as" with nothing after it. Zero is common — the keyword classifier writes
    // a 0.0 row for every comment to record that it ran, and the column itself
    // defaults to 0.0 — so this was the usual case, not an edge one. — Claude
    let classificationDisplay = ''
    let classificationPercentageValue = 0
    if (classification?.classification != null) {
        if (classification?.classifierName === SENTIMENT_NAME) {
            // The display is used for the tooltip
            classificationDisplay = (classification?.classification * 100).toFixed(0) + '%'
            classificationPercentageValue = -classification.classification
        } else {
            // The display is used for the tooltip
            classificationDisplay = (classification?.classification * 100).toFixed(0) + '%'
            classificationPercentageValue = classification.classification
        }
    }

    // Configure the radial progress settings
    const width = 25
    const height = 25
    const viewBox = `0 0 ${width} ${height}`
    const radius = 11
    const strokeWidth = 3
    const percentage = (classificationPercentageValue || 0) * 100
    // stroke-dasharray = circumference  = 2 * PI * radius
    const circumference = 2 * 3.141592 * radius
    // stroke-dashoffset = circumference - (circumference * percentage) / 100
    const offset = circumference - (circumference * percentage) / 100

    return (
        <div
            key={`${classification.classifierName}_${classification.classification}_${queue}_${accountId}_${contentId}`}
            className="flex flex-row "
        >
            <div
                data-tooltip-id="tooltip-id"
                data-tooltip-content={`${classifierName} classified as ${classificationDisplay.toLowerCase()}  `}
                className={`drop-shadow-sm inline-flex items-center gap-x-1.5 rounded-full  ${backgroundColour} ${textColour} border  ${borderColour} px-2 py-1 text-xs font-medium `}
            >
                <div className="font-semibold">{classifierName}</div>

                <div className="relative w-4 h-4">
                    <svg className="w-full h-full" viewBox={viewBox}>
                        <circle
                            className="text-gray-200 stroke-current"
                            strokeWidth={strokeWidth}
                            cx={width / 2}
                            cy={height / 2}
                            r={radius}
                            fill="transparent"
                        ></circle>

                        <circle
                            className={`${indicatorColour} progress-ring__circle stroke-current`}
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            cx={width / 2}
                            cy={height / 2}
                            r={radius}
                            fill="transparent"
                            strokeDasharray={circumference}
                            strokeDashoffset={offset}
                        ></circle>
                    </svg>
                </div>
            </div>
        </div>
    )
}
